// Resolve ícones de src/assets/img/icons-bu para categorias/subcategorias da API.
//
// Estrutura:
// - Pasta = categoria (slug). Arquivos dentro = subcategorias.
// - "Outros" usa o outros.svg na RAIZ da pasta icons-bu (compartilhado).
// - Categoria não tem ícone próprio: usa o ícone de uma subcategoria
//   representativa (CATEGORY_ICON), com fallback no primeiro arquivo da pasta.
//
// O casamento é por nome normalizado (sem acento/pontuação), então os nomes
// vindos da API devem bater com os nomes das pastas/arquivos.

const modules = import.meta.glob('../assets/img/icons-bu/**/*.svg', {
	eager: true,
	import: 'default'
})

const normalize = (value) =>
	(value || '')
		.toString()
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.trim()

// Subcategoria representativa por categoria (chaves e valores normalizados).
const CATEGORY_ICON = {
	'acessorios para eletrodomesticos': 'pecas e acessorios de cozinha',
	'ar condicionado': 'dual inverter split',
	'caixas de som bluetooth': 'caixas de som bluetooth para festa xboom',
	'geladeiras': 'geladeiras french door',
	'home audio': 'mini system xboom',
	'lavanderia': 'washtower',
	'micro ondas': 'micro ondas grill',
	'monitores': 'ultrawide',
	'projetores': 'icone projetores 64 cinebeam',
	'purificador de ar': 'puri care',
	'tvs e soundbars': 'oled evo'
}

let OUTROS = null
const buildIndex = () => {
	const index = {}
	for (const [path, url] of Object.entries(modules)) {
		const parts = path.split('/')
		const fileKey = normalize(parts.pop().replace(/\.svg$/i, ''))
		const folderName = parts.pop()
		if (folderName === 'icons-bu') { // outros.svg na raiz
			if (fileKey === 'outros') OUTROS = url
			continue
		}
		const folderKey = normalize(folderName)
		if (!index[folderKey]) index[folderKey] = { key: folderKey, files: {} }
		index[folderKey].files[fileKey] = url
	}
	return index
}
const INDEX = buildIndex()

// Nomes das pastas/arquivos são PT (BR). API de outros países (mx, cl, co,
// ec, pe, pa) retorna nome em ES, então cai aqui como segunda tentativa de
// casamento. Confirmado contra API mx; cl/co/ec/pe/pa assumidos com mesmo
// catálogo ES — se algum mercado usar termo diferente, adicionar entrada aqui.
const CATEGORY_ALIASES = {
	'refrigeradores': 'geladeiras',
	'aire acondicionado': 'ar condicionado',
	'altavoces bluetooth': 'caixas de som bluetooth',
	'audio para el hogar': 'home audio',
	'microondas': 'micro ondas',
	'otros': 'outros',
	'proyectores': 'projetores',
	'purificador de aire': 'purificador de ar',
	'accesorios para electrodomesticos': 'acessorios para eletrodomesticos',
	'tvs y barras de sonido': 'tvs e soundbars'
}

const SUBCATEGORY_ALIASES = {
	'congelador horizontal': 'freezer horizontal',
	'refrigerador side by side': 'geladeira side by side',
	'refrigeradores duplex': 'geladeiras duplex',
	'refrigeradores french door': 'geladeiras french door',
	'refrigeradores inverse': 'geladeiras inverse',
	'barras de sonido': 'soundbars',
	'barra de sonido home theater': 'soundbar home theater',
	'barra de sonido con subwoofer': 'soundbar com subwoofer',
	'piezas y accesorios para tv y audio': 'pecas e acessorios para tv e audio',
	'tv gaming': 'tv gamer',
	'microondas con grill': 'micro ondas grill',
	'microondas solo': 'micro ondas solo',
	'icono proyectores 64 cinebeam': 'icone projetores 64 cinebeam',
	'icono proyectores home cinema 64 normal': 'icone home cinema projetores 64 normal',
	'combos de monitores': 'combos monitores',
	'aire acondicionado multi split': 'ar condicionado multi split',
	'aire acondicionado portatil': 'ar condicionado portatil',
	'altavoces bluetooth para fiesta xboom': 'caixas de som bluetooth para festa xboom',
	'altavoces bluetooth portatiles xboom': 'caixas de som bluetooth portatil xboom',
	'torres de sonido xboom': 'caixas de torres de som xboom',
	'piezas y accesorios de cocina': 'pecas e acessorios de cozinha',
	'piezas y accesorios de lavanderia': 'pecas e acessorios de lavanderia',
	'lavadora y secadora': 'lava e seca',
	'lavadoras': 'maquinas de lavar',
	// Subcategorias legadas (createdAt 2024-01-01) só existentes em mx, sem
	// equivalente PT — apontam pro ícone genérico mais próximo da categoria.
	'residencial': 'dual inverter split',
	'audio': 'soundbars',
	'tv': 'tv uhd 4k',
	'otros': 'outros'
}

const resolveFolder = (categoryName) => {
	const key = normalize(categoryName)
	return INDEX[key] || INDEX[CATEGORY_ALIASES[key]] || null
}

export const getCategoryIcon = (categoryName) => {
	const folder = resolveFolder(categoryName)
	if (!folder) {
		const key = normalize(categoryName)
		return key === 'outros' || CATEGORY_ALIASES[key] === 'outros' ? OUTROS : null
	}

	const repKey = CATEGORY_ICON[folder.key]
	if (repKey && folder.files[repKey]) return folder.files[repKey]

	const first = Object.keys(folder.files)[0]
	return first ? folder.files[first] : null
}

export const getSubCategoryIcon = (categoryName, subName) => {
	const subKey = normalize(subName)
	if (subKey === 'outros' || SUBCATEGORY_ALIASES[subKey] === 'outros') return OUTROS
	const folder = resolveFolder(categoryName)
	if (!folder) return null
	return folder.files[subKey] || folder.files[SUBCATEGORY_ALIASES[subKey]] || null
}

export default { getCategoryIcon, getSubCategoryIcon }
